import moment from 'moment-timezone'

interface DateRange {
	fromDate: moment.Moment
	toDate: moment.Moment
}

/**
 * Get date range in a specific timezone
 * @param predefinedDateString - Date filter type
 * @param timezone - IANA timezone (e.g., 'America/New_York'). If undefined, uses moment() without timezone.
 */
export const getDateRange = (predefinedDateString: string, timezone?: string): DateRange => {
	// Create moment in the specified timezone, or use default if not provided
	const now = timezone ? moment.tz(timezone) : moment()
	let fromDate: moment.Moment, toDate: moment.Moment

	switch (predefinedDateString) {
		case 'today':
			fromDate = now.clone().startOf('day')
			toDate = now.clone().endOf('day')
			break

		case 'yesterday':
			fromDate = now.clone().subtract(1, 'day').startOf('day')
			toDate = now.clone().subtract(1, 'day').endOf('day')
			break

		case 'thisweek':
			fromDate = now.clone().startOf('week')
			toDate = now.clone().endOf('week')
			break

		case 'lastweek':
			fromDate = now.clone().subtract(1, 'week').startOf('week')
			toDate = now.clone().subtract(1, 'week').endOf('week')
			break

		case 'thismonth':
			fromDate = now.clone().startOf('month')
			toDate = now.clone().endOf('month')
			break

		case 'lastmonth':
			fromDate = now.clone().subtract(1, 'month').startOf('month')
			toDate = now.clone().subtract(1, 'month').endOf('month')
			break

		case 'thisyear':
			fromDate = now.clone().startOf('year')
			toDate = now.clone().endOf('year')
			break

		case 'lastyear':
			fromDate = now.clone().subtract(1, 'year').startOf('year')
			toDate = now.clone().subtract(1, 'year').endOf('year')
			break

		case 'past7days':
			fromDate = now.clone().subtract(7, 'days').startOf('day')
			toDate = now.clone().endOf('day')
			break

		case 'past30days':
			fromDate = now.clone().subtract(30, 'days').startOf('day')
			toDate = now.clone().endOf('day')
			break

		default:
			throw new Error('Invalid predefined date string')
	}

	return { fromDate, toDate }
}

/**
 * Parse and validate timezone from query parameter
 * @param timezone - Timezone string to validate
 * @returns Valid IANA timezone or undefined if not provided/invalid
 */
export const parseTimezone = (timezone: string | undefined): string | undefined => {
	// Validate timezone using moment-timezone
	if (!timezone || typeof timezone !== 'string' || !moment.tz.zone(timezone)) {
		return undefined
	}

	return timezone
}

/**
 * Format a date in a specific timezone
 * @param date - The date to format
 * @param timezone - IANA timezone (e.g., 'America/New_York'). If undefined, uses moment default.
 * @param format - Format string (defaults to 'YYYY-MM-DD HH:mm:ss')
 */
export const formatInTimezone = (date: Date | moment.Moment, timezone: string | undefined, format: string = 'YYYY-MM-DD HH:mm:ss'): string => {
	const m = moment(date)
	return timezone ? m.tz(timezone).format(format) : m.format(format)
}
